import { LightObject, LightModel, BookingDB } from "./BookingLightDB"
import { computed, observable, action } from "mobx"
import { uuid } from "../../light/utils"
import dayjs = require("dayjs")
import { Member, $Member } from "./Member"

export interface $Waiver extends LightObject {
  signDateString?: string
  signatureURL?: string
  externalId?: string
  externalSource?: string
  $memberAdult?: string
  $members: { [$id: string]: string }
  externalWaiverId?: string
  externalWaiverProviderName?: string
}

export class Waiver implements LightModel {
  constructor(db: BookingDB, object?: $Waiver, ownerId?: string) {
    this.db = db
    this.object = object ? object : Waiver.create$Waiver(ownerId)
  }

  @computed
  get id() {
    return this.object.$id
  }

  @computed
  get type() {
    return this.object.$type
  }

  db: BookingDB

  @computed
  get children() {
    return null
  }

  static create$Waiver($memberAdult?: string) {
    const object: $Waiver = {
      $id: uuid(),
      $type: "waiver",
      $version: uuid(),
      $parentVersion: null,
      $versionDateString: new Date().toISOString(),
      $memberAdult: $memberAdult,
      $members: {}
    }
    if ($memberAdult) {
      object.$members[$memberAdult] = $memberAdult
      object.$memberAdult = $memberAdult
    }
    return object
  }

  @observable
  file: BookingDB

  @observable
  object: $Waiver

  @action.bound
  addMember(member: Member) {
    if (member.isValid) {
      this.object.$members[member.object.$id] = member.object.$id
    } else throw new Error("Cannot add invalid waiver to booking")
  }

  @action.bound
  clean() {
    if (this.object.signDateString) {
      const signDateRaw = dayjs(this.object.signDateString)
      if (signDateRaw.isValid()) {
        this.object.signDateString = signDateRaw.toISOString()
      }
    }
  }

  @action.bound
  cleanAndCheck() {
    this.clean()
    if (!this.isValid) {
      throw new Error(this.invalidMessage)
    }
  }

  @computed
  get isValidSignature(): boolean {
    return this.signDate != undefined && this.signDate.isValid()
  }

  @computed
  get isValidSignatureData() {
    return (
      this.object.signatureURL != undefined &&
      this.object.signatureURL.length > 0
    )
  }

  @computed
  get invalidMessage(): string | undefined {
    if (!this.isValidSignature) return "Invalid signature"
    if (!this.isValidSignatureData) return "Invalid signature data"
    return ""
  }

  @computed
  get signDate() {
    return dayjs(this.object.signDateString)
  }

  @computed
  get memberAdult(): Member | null {
    if (this.object.$memberAdult)
      return new Member(this.db, this.db.file.objects[
        this.object.$memberAdult
      ] as $Member)
    return null
  }

  @computed
  get isSigned(): boolean {
    return this.isValid && this.object.signatureURL != null
  }

  @computed
  get isValid() {
    return this.signDate.isValid() && this.object.$id != null
  }

  @computed
  get members(): Member[] {
    if (this.object.$members)
      return Object.keys(this.object.$members).map(k => {
        const object = this.db.file.objects[k]
        return new Member(this.db, object as $Member)
      })
    return []
  }

  @action.bound
  sign(signatureURL: string) {
    this.object.signatureURL = signatureURL
    this.signWithDate()
  }

  @action.bound
  signWithDate() {
    if (!this.memberAdult)
      throw Error("Cannot sign waiver without an adult on the waiver")
    this.object.signDateString = new Date().toISOString()
    this.members.forEach(m => m.addWaiver(this))
  }
}
